import { useEffect, useReducer, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { AppointmentTaker } from '../../marketplace/types';
import type { TimeSlot } from '../services/appointmentService';
import {
  validateCoupon,
  applyCoupon,
  createStripeCheckout,
  pollStripeStatus,
  createBooking,
} from '../services/appointmentService';
import { CONSULTATION_SERVICE_ID } from '../../../config/api';
import { getDayOfWeekForCalendarDate, getMinSlotStartForToday, isCalendarDateToday, normalizeDateForApi } from '../../../utils/timezone';

export type AppointmentType = 'walk-in' | 'call';
export type PaymentStatus = 'idle' | 'pending' | 'success' | 'failed' | 'retry';

export interface BookerDetails {
  name: string;
  email: string;
  phone: string;
  countryCode: string;
  notes: string;
}

export interface PricingBreakdown {
  base: number;
  hst: number;
  total: number;
  currency: string;
}

export interface CouponState {
  code: string;
  discount: number;
  isFree: boolean;
  applied: boolean;
  error: string | null;
  loading: boolean;
}

export interface ConfirmationData {
  appointmentId?: string;
  displayName: string;
  date: Date;
  slotStart: Date;
  slotEnd: Date;
  type: AppointmentType;
  bookerName: string;
  bookerEmail: string;
  officeAddress?: string;
  bookingReference: string;
}

interface BookingState {
  activeStep: number;
  appointmentType: AppointmentType;
  date: Date;
  selectedSlot: TimeSlot | null;
  bookerDetails: BookerDetails;
  bookingReference: string;
  coupon: CouponState;
  pricing: PricingBreakdown;
  paymentStatus: PaymentStatus;
  paymentError: string | null;
  confirmationData: ConfirmationData | null;
  stepError: string | null;
}

type BookingAction =
  | { type: 'SET_STEP'; step: number }
  | { type: 'SET_TYPE'; appointmentType: AppointmentType }
  | { type: 'SET_DATE'; date: Date }
  | { type: 'SET_SLOT'; slot: TimeSlot | null }
  | { type: 'SET_BOOKER'; bookerDetails: Partial<BookerDetails> }
  | { type: 'SET_COUPON_CODE'; code: string }
  | { type: 'SET_COUPON'; coupon: Partial<CouponState> }
  | { type: 'SET_PRICING'; pricing: Partial<PricingBreakdown> }
  | { type: 'SET_PAYMENT_STATUS'; status: PaymentStatus; error?: string }
  | { type: 'SET_CONFIRMATION'; data: ConfirmationData }
  | { type: 'SET_STEP_ERROR'; error: string | null }
  | { type: 'SET_REF'; ref: string }
  | { type: 'PREFILL_BOOKER'; client: { name?: string; email?: string; phone?: string; country_code?: string } };


export function getTodayInTimeZone(timeZone: string): Date {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const y = parseInt(parts.find((p) => p.type === 'year')!.value, 10);
  const m = parseInt(parts.find((p) => p.type === 'month')!.value, 10);
  const d = parseInt(parts.find((p) => p.type === 'day')!.value, 10);
  return new Date(y, m - 1, d);
}

const getInitialState = (): BookingState => ({
  activeStep: 0,
  appointmentType: 'walk-in',
  date: new Date(),
  selectedSlot: null,
  bookerDetails: { name: '', email: '', phone: '', countryCode: '+1', notes: '' },
  bookingReference: '',
  coupon: { code: '', discount: 0, isFree: false, applied: false, error: null, loading: false },
  pricing: { base: 0, hst: 0, total: 0, currency: 'CAD' },
  paymentStatus: 'idle',
  paymentError: null,
  confirmationData: null,
  stepError: null,
});

function reducer(state: BookingState, action: BookingAction): BookingState {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, activeStep: action.step, stepError: null };
    case 'SET_TYPE':
      if (state.appointmentType === action.appointmentType) return state;
      return { ...state, appointmentType: action.appointmentType, selectedSlot: null, stepError: null };
    case 'SET_DATE':
      return { ...state, date: normalizeDateForApi(action.date), selectedSlot: null };
    case 'SET_SLOT':
      return { ...state, selectedSlot: action.slot, stepError: null };
    case 'SET_BOOKER':
      return { ...state, bookerDetails: { ...state.bookerDetails, ...action.bookerDetails } };
    case 'SET_COUPON_CODE':
      return { ...state, coupon: { ...state.coupon, code: action.code, error: null } };
    case 'SET_COUPON':
      return { ...state, coupon: { ...state.coupon, ...action.coupon } };
    case 'SET_PRICING':
      return { ...state, pricing: { ...state.pricing, ...action.pricing } };
    case 'SET_PAYMENT_STATUS':
      return { ...state, paymentStatus: action.status, paymentError: action.error ?? null };
    case 'SET_CONFIRMATION':
      return { ...state, confirmationData: action.data, paymentStatus: 'success' };
    case 'SET_STEP_ERROR':
      return { ...state, stepError: action.error };
    case 'SET_REF':
      return { ...state, bookingReference: action.ref };
    case 'PREFILL_BOOKER': {
      const c = action.client;
      return {
        ...state,
        bookerDetails: {
          name: state.bookerDetails.name || c.name || '',
          email: state.bookerDetails.email || c.email || '',
          phone: state.bookerDetails.phone || (c.phone ? c.phone.replace(/\D/g, '').slice(-10) : ''),
          countryCode: state.bookerDetails.countryCode || c.country_code || '+1',
          notes: state.bookerDetails.notes,
        },
      };
    }
    default:
      return state;
  }
}

const STORAGE_KEY = (ref: string) => `cwy-booking-${ref}`;
const SUCCESS_KEY = (ref: string) => `cwy-booking-success-${ref}`;

export const HST_RATE = 0.13;
export const WALK_IN_BUFFER_MINUTES = 30;

export function computePricing(fee: number, currency: string, discount = 0): PricingBreakdown {
  const base = Math.max(0, fee - discount);
  const hst = currency === 'CAD' ? parseFloat((base * HST_RATE).toFixed(2)) : 0;
  const total = parseFloat((base + hst).toFixed(2));
  return { base, hst, total, currency };
}

export function validateEmail(email: string): string | null {
  const trimmed = email.trim();
  if (!trimmed) return 'Email is required';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(trimmed)) {
    return 'Enter a valid email address (e.g. name@example.com)';
  }
  return null;
}

export function validatePhone(phone: string, countryCode: string): string | null {
  const digits = phone.replace(/\D/g, '');
  if (countryCode === '+1' || countryCode === '+91') {
    return digits.length === 10 ? null : 'Phone must be 10 digits';
  }
  return digits.length >= 4 && digits.length <= 15 ? null : 'Phone must be 4–15 digits';
}

export function isWithinBusinessHours(
  date: Date,
  taker: AppointmentTaker
): boolean {
  const bh = taker.businessHours;
  const tz = taker.timeZone || 'America/Toronto';

  let startH: number, startM: number, endH: number, endM: number;
  if (bh) {
    startH = bh.start;
    startM = bh.startMinute;
    endH = bh.end;
    endM = bh.endMinute;
  } else {
    startH = 11;
    startM = 0;
    endH = 18;
    endM = 0;
  }

  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(date);
  const h = parseInt(parts.find((p) => p.type === 'hour')!.value);
  const m = parseInt(parts.find((p) => p.type === 'minute')!.value);
  const nowM = h * 60 + m;
  return nowM >= startH * 60 + startM && nowM < endH * 60 + endM;
}

export function isToday(date: Date, timeZone: string): boolean {
  return isCalendarDateToday(date, timeZone);
}

export function getMinTimeForToday(timeZone?: string): Date {
  const tz = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  return getMinSlotStartForToday(tz, 30);
}

/** @deprecated use getMinTimeForToday */
export const getComingLaterMinTime = getMinTimeForToday;

export function getNextAvailableDate(startDate: Date, taker: AppointmentTaker): Date {
  const tz = taker.timeZone || 'America/Toronto';
  const next = normalizeDateForApi(startDate);
  next.setDate(next.getDate() + 1);

  while (true) {
    const day = getDayOfWeekForCalendarDate(next, tz);
    if (day === 6 && !taker.allowSaturday) {
      next.setDate(next.getDate() + 1);
      continue;
    }
    if (day === 0 && !taker.allowSunday) {
      next.setDate(next.getDate() + 1);
      continue;
    }
    break;
  }
  return next;
}

export function useBookingFlow(taker: AppointmentTaker | null) {
  const [state, dispatch] = useReducer(reducer, undefined, getInitialState);
  const [searchParams] = useSearchParams();
  const stripeHandledRef = useRef<string | null>(null);
  const syncedDateForUserIdRef = useRef<string | null>(null);

  // Sync booking date to the consultant's calendar "today" whenever the taker changes
  useEffect(() => {
    if (!taker?.userId) return;
    if (syncedDateForUserIdRef.current === taker.userId) return;
    syncedDateForUserIdRef.current = taker.userId;
    const tz = taker.timeZone || 'America/Toronto';
    dispatch({ type: 'SET_DATE', date: getTodayInTimeZone(tz) });
  }, [taker?.userId, taker?.timeZone]);

  // Auth prefill is handled by the page component by calling prefillFromClient

  // On mount: check for Stripe return
  useEffect(() => {
    const ref = searchParams.get('ref');
    const sessionId = searchParams.get('session_id');
    const cancelled = searchParams.get('cancelled');

    if (ref && sessionId && stripeHandledRef.current !== sessionId) {
      stripeHandledRef.current = sessionId;
      handleStripeReturn(ref, sessionId);
    } else if (ref && cancelled === '1') {
      dispatch({ type: 'SET_STEP', step: 2 });
      dispatch({ type: 'SET_PAYMENT_STATUS', status: 'idle', error: 'Payment was cancelled — you can try again.' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recalculate pricing when taker or coupon changes
  useEffect(() => {
    if (!taker) return;
    const discount = state.coupon.applied ? state.coupon.discount : 0;
    dispatch({
      type: 'SET_PRICING',
      pricing: computePricing(taker.fee, taker.currency, discount),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taker?.fee, taker?.currency, state.coupon.applied, state.coupon.discount]);

  // Default appointment type when taker loads
  useEffect(() => {
    if (!taker) return;
    if (taker.allowWalkIn !== false) {
      dispatch({ type: 'SET_TYPE', appointmentType: 'walk-in' });
    } else if (taker.allowCall !== false) {
      dispatch({ type: 'SET_TYPE', appointmentType: 'call' });
    }
  }, [taker?.userId]);

  function prefillFromClient(client: { name?: string; email?: string; phone?: string; country_code?: string }) {
    dispatch({ type: 'PREFILL_BOOKER', client });
  }

  function canProceedFromStep(step: number): { ok: boolean; reason?: string } {
    if (step === 0) {
      if (!taker) return { ok: false, reason: 'Consultant not loaded' };
      if (taker.allowWalkIn === false && taker.allowCall === false) {
        return { ok: false, reason: 'This consultant is not currently accepting bookings.' };
      }
      if (state.appointmentType === 'walk-in' && taker.allowWalkIn === false) {
        return { ok: false, reason: 'Walk-in appointments are not available for this consultant.' };
      }
      if (state.appointmentType === 'call' && taker.allowCall === false) {
        return { ok: false, reason: 'Phone call appointments are not available for this consultant.' };
      }
      if (!state.selectedSlot) return { ok: false, reason: 'Please select a time slot.' };

      const tz = taker.timeZone || 'America/Toronto';
      const todayInTz = isToday(state.date, tz);

      if (todayInTz) {
        const minTime = getMinTimeForToday();
        if (state.selectedSlot.start < minTime) {
          return { ok: false, reason: 'Please select a future time slot.' };
        }
      } else if (!isWithinBusinessHours(state.selectedSlot.start, taker)) {
        return { ok: false, reason: 'Selected slot is outside office hours.' };
      }

      return { ok: true };
    }
    if (step === 1) {
      const { name, email, phone, countryCode } = state.bookerDetails;
      if (!name.trim() || name.trim().length < 2) return { ok: false, reason: 'Name must be at least 2 characters.' };
      const emailErr = validateEmail(email);
      if (emailErr) return { ok: false, reason: emailErr };
      const phoneErr = validatePhone(phone, countryCode);
      if (phoneErr) return { ok: false, reason: phoneErr };
      return { ok: true };
    }
    return { ok: true };
  }

  function goNext() {
    const check = canProceedFromStep(state.activeStep);
    if (!check.ok) {
      dispatch({ type: 'SET_STEP_ERROR', error: check.reason ?? 'Please complete this step.' });
      return;
    }
    dispatch({ type: 'SET_STEP', step: state.activeStep + 1 });
  }

  function goBack() {
    if (state.activeStep > 0) {
      dispatch({ type: 'SET_STEP', step: state.activeStep - 1 });
    }
  }

  async function applyPromoCode() {
    if (!state.coupon.code.trim() || !taker) return;
    dispatch({ type: 'SET_COUPON', coupon: { loading: true, error: null } });
    try {
      const result = await validateCoupon({
        code: state.coupon.code.trim(),
        email: state.bookerDetails.email,
        amount: taker.fee,
        currency: taker.currency,
      });
      if (result.valid) {
        dispatch({
          type: 'SET_COUPON',
          coupon: {
            loading: false,
            applied: true,
            discount: result.discount ?? 0,
            isFree: result.isFree ?? false,
            error: null,
          },
        });
      } else {
        dispatch({ type: 'SET_COUPON', coupon: { loading: false, error: result.message || 'Invalid coupon code.' } });
      }
    } catch {
      dispatch({ type: 'SET_COUPON', coupon: { loading: false, error: 'Could not validate coupon.' } });
    }
  }

  async function handlePayNow(slug: string) {
    if (!taker || !state.selectedSlot) return;

    const ref =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `ref-${Date.now()}-${Math.floor(Math.random() * 100000)}`;
    dispatch({ type: 'SET_REF', ref });
    dispatch({ type: 'SET_PAYMENT_STATUS', status: 'pending' });

    const phone = `${state.bookerDetails.countryCode}${state.bookerDetails.phone.replace(/\D/g, '')}`;
    const payload = {
      name: state.bookerDetails.name.trim(),
      email: state.bookerDetails.email.trim(),
      phone,
      countryCode: state.bookerDetails.countryCode,
      notes: state.bookerDetails.notes.trim(),
      booker_msg: state.bookerDetails.notes.trim(),
      date: state.selectedSlot.start.toISOString(),
      slotStart: state.selectedSlot.start.toISOString(),
      slotEnd: state.selectedSlot.end.toISOString(),
      appointmentType: state.appointmentType,
      serviceId: CONSULTATION_SERVICE_ID,
      appointmentForUserId: taker.userId,
      appointmentForEmail: taker.email,
      bookingReference: ref,
      country: taker.currency === 'INR' ? 'IN' : 'CA',
      timeZone: taker.timeZone || 'America/Toronto',
      couponCode: state.coupon.applied ? state.coupon.code.trim() || undefined : undefined,
    };

    localStorage.setItem(STORAGE_KEY(ref), JSON.stringify(payload));

    const origin = window.location.origin;
    try {
      const result = await createStripeCheckout({
        name: payload.name,
        phone,
        email: payload.email,
        country: 'CA',
        amount: state.pricing.total,
        currency: 'CAD',
        // Ledger description is forced server-side for bookings; client notes stay on Appointment.
        message: 'Stripe Client Sale',
        successUrl: `${origin}/appointments/${slug}/book?ref=${encodeURIComponent(ref)}&session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${origin}/appointments/${slug}/book?ref=${encodeURIComponent(ref)}&cancelled=1`,
        bookingReference: ref,
      });
      window.location.href = result.sessionUrl;
    } catch (err) {
      dispatch({
        type: 'SET_PAYMENT_STATUS',
        status: 'failed',
        error: err instanceof Error ? err.message : 'Unable to start payment.',
      });
    }
  }

  async function handleBookFree() {
    if (!taker || !state.selectedSlot) return;
    const ref =
      state.bookingReference ||
      (typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `ref-${Date.now()}`);
    if (!state.bookingReference) dispatch({ type: 'SET_REF', ref });
    dispatch({ type: 'SET_PAYMENT_STATUS', status: 'pending' });

    const phone = `${state.bookerDetails.countryCode}${state.bookerDetails.phone.replace(/\D/g, '')}`;
    const payload = {
      name: state.bookerDetails.name.trim(),
      email: state.bookerDetails.email.trim(),
      phone,
      countryCode: state.bookerDetails.countryCode,
      notes: state.bookerDetails.notes.trim(),
      booker_msg: state.bookerDetails.notes.trim(),
      date: state.selectedSlot.start.toISOString(),
      slotStart: state.selectedSlot.start.toISOString(),
      slotEnd: state.selectedSlot.end.toISOString(),
      appointmentType: state.appointmentType,
      serviceId: CONSULTATION_SERVICE_ID,
      appointmentForUserId: taker.userId,
      appointmentForEmail: taker.email,
      bookingReference: ref,
      country: taker.currency === 'INR' ? 'IN' : 'CA',
      timeZone: taker.timeZone || 'America/Toronto',
      couponCode: state.coupon.applied ? state.coupon.code.trim() || undefined : undefined,
    };

    try {
      if (state.coupon.applied && state.coupon.code) {
        await applyCoupon({ code: state.coupon.code, email: payload.email, bookingReference: ref });
      }
      const result = await createBooking(payload);
      localStorage.removeItem(STORAGE_KEY(ref));
      localStorage.setItem(SUCCESS_KEY(ref), JSON.stringify({ ref, confirmed: true }));
      dispatch({
        type: 'SET_CONFIRMATION',
        data: {
          appointmentId: result.appointmentId,
          displayName: taker.displayName,
          date: state.date,
          slotStart: state.selectedSlot.start,
          slotEnd: state.selectedSlot.end,
          type: state.appointmentType,
          bookerName: payload.name,
          bookerEmail: payload.email,
          officeAddress: taker.officeAddress?.fullAddress,
          bookingReference: ref,
        },
      });
      dispatch({ type: 'SET_STEP', step: 3 });
    } catch (err) {
      dispatch({
        type: 'SET_PAYMENT_STATUS',
        status: 'failed',
        error: err instanceof Error ? err.message : 'Failed to create booking.',
      });
    }
  }

  async function handleStripeReturn(ref: string, sessionId: string) {
    dispatch({ type: 'SET_STEP', step: 2 });
    dispatch({ type: 'SET_PAYMENT_STATUS', status: 'pending' });

    // Check if already confirmed (page refresh)
    const existing = localStorage.getItem(SUCCESS_KEY(ref));
    if (existing) {
      const parsed = JSON.parse(existing);
      if (parsed.confirmed) {
        dispatch({ type: 'SET_STEP', step: 3 });
        dispatch({ type: 'SET_PAYMENT_STATUS', status: 'success' });
        return;
      }
    }

    try {
      await pollStripeStatus(sessionId);

      const rawPayload = localStorage.getItem(STORAGE_KEY(ref));
      if (!rawPayload) {
        throw new Error('Booking details not found. Please contact support.');
      }
      const payload = JSON.parse(rawPayload);
      const result = await createBooking(payload);

      // Only delete after confirmed success
      localStorage.removeItem(STORAGE_KEY(ref));
      localStorage.setItem(SUCCESS_KEY(ref), JSON.stringify({ ref, confirmed: true }));

      const slotStart = new Date(payload.slotStart);
      const slotEnd = new Date(payload.slotEnd);

      dispatch({
        type: 'SET_CONFIRMATION',
        data: {
          appointmentId: result.appointmentId,
          displayName: taker?.displayName ?? 'Consultant',
          date: new Date(payload.date),
          slotStart,
          slotEnd,
          type: payload.appointmentType,
          bookerName: payload.name,
          bookerEmail: payload.email,
          officeAddress: taker?.officeAddress?.fullAddress,
          bookingReference: ref,
        },
      });
      dispatch({ type: 'SET_STEP', step: 3 });
    } catch (err) {
      dispatch({
        type: 'SET_PAYMENT_STATUS',
        status: 'retry',
        error: err instanceof Error ? err.message : 'Booking creation failed.',
      });
    }
  }

  async function retryCreateBooking() {
    if (!state.bookingReference) return;
    const rawPayload = localStorage.getItem(STORAGE_KEY(state.bookingReference));
    if (!rawPayload) {
      dispatch({ type: 'SET_PAYMENT_STATUS', status: 'failed', error: 'Booking data not found. Please contact support.' });
      return;
    }
    dispatch({ type: 'SET_PAYMENT_STATUS', status: 'pending' });
    try {
      const payload = JSON.parse(rawPayload);
      const result = await createBooking(payload);
      localStorage.removeItem(STORAGE_KEY(state.bookingReference));
      localStorage.setItem(SUCCESS_KEY(state.bookingReference), JSON.stringify({ ref: state.bookingReference, confirmed: true }));
      dispatch({
        type: 'SET_CONFIRMATION',
        data: {
          appointmentId: result.appointmentId,
          displayName: taker?.displayName ?? 'Consultant',
          date: new Date(payload.date),
          slotStart: new Date(payload.slotStart),
          slotEnd: new Date(payload.slotEnd),
          type: payload.appointmentType,
          bookerName: payload.name,
          bookerEmail: payload.email,
          officeAddress: taker?.officeAddress?.fullAddress,
          bookingReference: state.bookingReference,
        },
      });
      dispatch({ type: 'SET_STEP', step: 3 });
    } catch (err) {
      dispatch({ type: 'SET_PAYMENT_STATUS', status: 'retry', error: err instanceof Error ? err.message : 'Retry failed.' });
    }
  }

  return {
    state,
    dispatch,
    prefillFromClient,
    canProceedFromStep,
    goNext,
    goBack,
    applyPromoCode,
    handlePayNow,
    handleBookFree,
    handleStripeReturn,
    retryCreateBooking,
  };
}
